import React from 'react';
import { CheckCircleIcon, KeyIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const VenueCompletionStep = ({ venueAlreadyExists, venueName, shortCodeKey, onNavigateToActivation }) => {
    return (
        <div className="text-center space-y-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100">
                <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
            </div>

            <div>
                <h3 className="text-2xl font-bold text-white mb-2">
                    {venueAlreadyExists ? 'Payment Setup Complete!' : 'Venue Created Successfully!'}
                </h3>
                <p className="text-gray-400">
                    Your venue "{venueName}" has been set up with the short code "{shortCodeKey}".
                </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-left">
                <h4 className="text-lg font-semibold text-white mb-4">Next Steps to Activate Your Venue:</h4>
                
                <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                1
                            </div>
                        </div>
                        <div>
                            <h5 className="font-medium text-white">Check Your Email</h5>
                            <p className="text-gray-400 text-sm">
                                We've sent activation instructions to your contact email. Please check your inbox and spam folder.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                2
                            </div>
                        </div>
                        <div>
                            <h5 className="font-medium text-white">Verify Your Account</h5>
                            <p className="text-gray-400 text-sm">
                                Click the verification link in the email to activate your venue account.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                3
                            </div>
                        </div>
                        <div>
                            <h5 className="font-medium text-white">First Time Login</h5>
                            <p className="text-gray-400 text-sm">
                                Use your email and password to sign in to your venue dashboard for the first time.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                4
                            </div>
                        </div>
                        <div>
                            <h5 className="font-medium text-white">Complete Setup</h5>
                            <p className="text-gray-400 text-sm">
                                Add your classes, configure your venue settings, and start accepting bookings.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-center mb-3">
                        <KeyIcon className="h-6 w-6 text-emerald-400" />
                    </div>
                    <h4 className="font-medium text-white mb-2">Your Venue Code</h4>
                    <p className="text-emerald-400 font-mono text-lg">{shortCodeKey}</p>
                    <p className="text-gray-400 text-sm mt-1">Share this with your users for easy signup</p>
                </div>

                <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-center mb-3">
                        <EnvelopeIcon className="h-6 w-6 text-blue-400" />
                    </div>
                    <h4 className="font-medium text-white mb-2">Support</h4>
                    <p className="text-gray-400 text-sm">
                        Need help? Contact our support team for assistance with setup and onboarding.
                    </p>
                </div>
            </div>

            <div className="bg-emerald-900/20 border border-emerald-700 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-emerald-400 mt-0.5" />
                    <div className="text-left">
                        <h4 className="text-emerald-400 font-medium text-sm">Payment Setup Complete</h4>
                        <p className="text-emerald-300 text-sm mt-1">
                            Your payment method has been successfully configured. Your first billing cycle will begin 
                            once your account is activated and you start using the platform.
                        </p>
                    </div>
                </div>
            </div>

            <div className="pt-4">
                <button
                    onClick={onNavigateToActivation || (() => window.location.href = '/login')}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                >
                    Continue to Activation
                </button>
            </div>
        </div>
    );
};

export default VenueCompletionStep;
